/* ──────────────────────────────────────────────────────────
   Playground adapter — wraps existing exercises from
   src/data/sql/exercises.ts  and  src/data/python/exercises.ts
   into the shape the LessonPlayground component expects.
   ────────────────────────────────────────────────────────── */

import { sqlExercises } from './sql/exercises';
import { pythonExercises } from './python/exercises';
import { pythonTopics } from './python/topics';
import { introduccionTopic } from './python/sections/introduccion';
import { estructurasFuncionesTopic } from './python/sections/estructuras-funciones';
import { contenedoresErroresTopic } from './python/sections/contenedores-errores';
import { trabajandoConDatosTopic } from './python/sections/trabajando-con-datos';
import { algoritmosListasTopic } from './python/sections/algoritmos-listas';
import { aleatoriedadTopic } from './python/sections/aleatoriedad';
import { complejidadOrganizacionTopic } from './python/sections/complejidad-organizacion';
import { disenoEspecificacionTopic } from './python/sections/diseno-especificacion';
import { fechasCarpetasTopic } from './python/sections/fechas-carpetas';
import { clasesObjetosTopic } from './python/sections/clases-objetos';
import { deExercises } from './data-engineering/exercises';
import { deRankingData } from './data-engineering/ranking-data';
import { examTechnicalExercises } from './sql/exam-technical';
import { linkedInTopics } from './linkedin/topics';
import { linkedInRankingData } from './linkedin/ranking-data';
import { adaptBootcampDE, type CohortFlags } from './bootcamp/bootcamp-de-lessons';

/* ── Public types consumed by LessonPlayground ── */

export interface TestCase {
  id: number;
  name: string;
  input: Record<string, any>;
  expectedOutput: any;
  description?: string;
}

export interface RankingOption {
  id: number;
  text: string;
}

export interface RankingData {
  question: string;
  options: RankingOption[];
  correctOrder: number[];
  explanation: string;
}

export interface DownloadItem {
  label: string;
  url: string;
  type: 'exercise' | 'solution' | 'dataset' | 'help' | 'interview' | 'glossary' | 'other';
}

export interface QuizQuestion {
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
}

export interface PlaygroundLesson {
  title: string;
  level: string;
  starterCode: string;
  solutionCode: string;
  sampleOutput: string;
  testCases?: TestCase[];
  ranking?: RankingData;
  theoricClassVideoUrl?: string;
  groupClassVideoUrl?: string;
  downloads?: DownloadItem[];
  quiz?: QuizQuestion[];
  availableFrom?: string;
  theory: {
    heading: string;
    intro: string;
    sections: { title: string; body: string }[];
  };
  exercise: {
    heading: string;
    instructions: string[];
    hint?: string;
  };
}

export interface PlaygroundCourse {
  courseId: string;
  courseTitle: string;
  language: 'python' | 'sql' | 'text';
  lessons: PlaygroundLesson[];
  database?: string;
  derImage?: string;
  nextCourseId?: string;
}

/* ── Adapters ── */

const sqlVideoUrls: Record<number, string> = {
  0: '/videos/cursos/sql/01_select_teoria_y_practica.mp4',
};

function adaptSql(): PlaygroundLesson[] {
  return sqlExercises.map((ex, idx) => {
    const sections: { title: string; body: string }[] = [];
    if (ex.why) {
      sections.push({ title: ex.why.title || '¿Por qué?', body: ex.why.content });
      if (ex.why.keyPoints && ex.why.keyPoints.length > 0) {
        sections.push({
          title: 'Puntos clave',
          body: ex.why.keyPoints.map(p => `- ${p}`).join('\n'),
        });
      }
      if (ex.why.realWorldExample) {
        sections.push({ title: 'En el mundo real', body: ex.why.realWorldExample });
      }
    }

    // Exercise instructions from description + expected columns
    const instructions: string[] = [ex.description];
    if (ex.expectedColumns.length > 0) {
      instructions.push(`Columnas esperadas: \`${ex.expectedColumns.join('`, `')}\``);
    }

    return {
      title: ex.title,
      level: ex.level,
      starterCode: `-- ${ex.description}\n\n`,
      solutionCode: ex.solution,
      sampleOutput: `Query ejecutada correctamente.\nColumnas: ${ex.expectedColumns.join(', ')}`,
      theoricClassVideoUrl: sqlVideoUrls[idx],
      theory: {
        heading: ex.title,
        intro: ex.description,
        sections,
      },
      exercise: {
        heading: ex.title,
        instructions,
        hint: ex.tips && ex.tips.length > 0 ? ex.tips[0] : undefined,
      },
    };
  });
}

function adaptPythonSubset(topics: typeof pythonTopics): PlaygroundLesson[] {
  const lessons: PlaygroundLesson[] = [];

  for (const topic of topics) {
    if (topic.id === -1) continue;

    for (const ex of topic.exercises) {
      const sections: { title: string; body: string }[] = [];

      if (ex.theory) {
        sections.push({ title: 'Teoría', body: ex.theory });
      }

      if (ex.why) {
        sections.push({ title: ex.why.title || '¿Por qué?', body: ex.why.content });
        if (ex.why.keyPoints && ex.why.keyPoints.length > 0) {
          sections.push({
            title: 'Puntos clave',
            body: ex.why.keyPoints.map(p => `- ${p}`).join('\n'),
          });
        }
        if (ex.why.realWorldExample) {
          sections.push({ title: 'En el mundo real', body: ex.why.realWorldExample });
        }
      }

      if (ex.tips && ex.tips.length > 0) {
        sections.push({ title: 'Tips', body: ex.tips.map(t => `- ${t}`).join('\n') });
      }
      if (ex.advice && ex.advice.length > 0) {
        sections.push({ title: 'Consejos', body: ex.advice.map(a => `- ${a}`).join('\n') });
      }

      const firstTest = ex.testCases[0];
      const sampleOutput = firstTest
        ? `>>> Resultado esperado: ${JSON.stringify(firstTest.expectedOutput)}`
        : '>>> Ejecutar para ver resultado';

      lessons.push({
        title: ex.title,
        level: topic.title,
        starterCode: ex.initialCode || `# ${ex.description}\n\n`,
        solutionCode: ex.solution,
        sampleOutput,
        testCases: ex.testCases.length > 0
          ? ex.testCases.map(tc => ({ id: tc.id, name: tc.name, input: tc.input, expectedOutput: tc.expectedOutput, description: tc.description }))
          : undefined,
        theory: {
          heading: ex.title,
          intro: ex.description,
          sections,
        },
        exercise: {
          heading: ex.title,
          instructions: [
            ex.description,
            ...(ex.testCases.length > 0
              ? ex.testCases.map(tc => tc.description || `${tc.name}: Input ${JSON.stringify(tc.input)} → ${JSON.stringify(tc.expectedOutput)}`)
              : []),
          ],
          hint: ex.hints && ex.hints.length > 0 ? ex.hints[0] : undefined,
        },
      });
    }
  }

  return lessons;
}

function adaptPythonIntro(): PlaygroundLesson[] {
  return adaptPythonSubset([introduccionTopic, estructurasFuncionesTopic, contenedoresErroresTopic]);
}

function adaptPythonIntermedio(): PlaygroundLesson[] {
  return adaptPythonSubset([trabajandoConDatosTopic, algoritmosListasTopic, aleatoriedadTopic]);
}

function adaptPythonAvanzado(): PlaygroundLesson[] {
  return adaptPythonSubset([complejidadOrganizacionTopic, disenoEspecificacionTopic, fechasCarpetasTopic, clasesObjetosTopic]);
}

function adaptPythonExam(): PlaygroundLesson[] {
  return pythonExercises.map(ex => {
    const sections: { title: string; body: string }[] = [
      { title: 'Objetivo', body: ex.description },
    ];

    if (ex.testCases.length > 0) {
      const casesBody = ex.testCases
        .map(tc => `- **${tc.name}**: ${tc.description || `Input: ${JSON.stringify(tc.input)} → Expected: ${JSON.stringify(tc.expectedOutput)}`}`)
        .join('\n');
      sections.push({ title: 'Casos de prueba', body: casesBody });
    }

    const firstTest = ex.testCases[0];
    const sampleOutput = firstTest
      ? `>>> Resultado esperado: ${JSON.stringify(firstTest.expectedOutput)}`
      : '>>> Ejecutar para ver resultado';

    return {
      title: ex.title,
      level: ex.level,
      starterCode: ex.initialCode || `# ${ex.description}\n\n`,
      solutionCode: ex.solution,
      sampleOutput,
      testCases: ex.testCases.map(tc => ({ id: tc.id, name: tc.name, input: tc.input, expectedOutput: tc.expectedOutput, description: tc.description })),
      theory: {
        heading: ex.title,
        intro: ex.description,
        sections,
      },
      exercise: {
        heading: ex.title,
        instructions: [
          ex.description,
          ...ex.testCases.map(tc => tc.description || `${tc.name}: Input ${JSON.stringify(tc.input)} → ${JSON.stringify(tc.expectedOutput)}`),
        ],
      },
    };
  });
}

function adaptDE(): PlaygroundLesson[] {
  return deExercises.map(ex => ({
    title: ex.title,
    level: ex.level,
    starterCode: `# Escribí tu respuesta acá\n\n`,
    solutionCode: ex.modelAnswer,
    sampleOutput: '',
    ranking: deRankingData[ex.id],
    theory: {
      heading: ex.title,
      intro: ex.description,
      sections: [{ title: 'Contenido', body: ex.theory }],
    },
    exercise: {
      heading: ex.exercise.heading,
      instructions: ex.exercise.instructions,
      hint: ex.exercise.hint,
    },
  }));
}

function adaptExamSql(): PlaygroundLesson[] {
  return examTechnicalExercises.map(ex => {
    const instructions: string[] = [ex.description];
    if (ex.expectedColumns.length > 0) {
      instructions.push(`Columnas esperadas: \`${ex.expectedColumns.join('`, `')}\``);
    }

    return {
      title: ex.title,
      level: ex.level,
      starterCode: `-- ${ex.title}\n-- ${ex.description.split('\n')[0]}\n\n`,
      solutionCode: ex.solution,
      sampleOutput: `Query ejecutada correctamente.\nColumnas: ${ex.expectedColumns.join(', ')}`,
      theory: {
        heading: ex.title,
        intro: ex.description,
        sections: [{
          title: 'Consigna',
          body: ex.description.replace(/\n/g, '\n\n'),
        }],
      },
      exercise: {
        heading: ex.title,
        instructions,
        hint: ex.tips && ex.tips.length > 0 ? ex.tips[0] : undefined,
      },
    };
  });
}

function adaptLinkedIn(): PlaygroundLesson[] {
  return linkedInTopics.map(topic => {
    const sections: { title: string; body: string }[] = [];

    if (topic.content.overview) {
      sections.push({ title: 'Visión general', body: topic.content.overview });
    }
    if (topic.content.why) {
      sections.push({ title: '¿Por qué?', body: topic.content.why });
    }
    if (topic.content.how) {
      sections.push({ title: '¿Cómo?', body: topic.content.how });
    }

    if (topic.content.keyConcepts?.length > 0) {
      for (const concept of topic.content.keyConcepts) {
        const body = [
          concept.description,
          '',
          ...concept.characteristics.map(c => `- ${c}`),
          ...(concept.examples?.length ? ['', '**Ejemplos:**', ...concept.examples.map(e => `- ${e}`)] : []),
        ].join('\n');
        sections.push({ title: concept.name, body });
      }
    }

    if (topic.content.comparisons?.length) {
      for (const comp of topic.content.comparisons) {
        const header = comp.items[0]?.option3
          ? `| Aspecto | Opción 1 | Opción 2 | Opción 3 |`
          : `| Aspecto | Opción 1 | Opción 2 |`;
        const sep = comp.items[0]?.option3
          ? `|---------|----------|----------|----------|`
          : `|---------|----------|----------|`;
        const rows = comp.items.map(it =>
          it.option3
            ? `| ${it.aspect} | ${it.option1} | ${it.option2} | ${it.option3} |`
            : `| ${it.aspect} | ${it.option1} | ${it.option2} |`
        );
        sections.push({ title: comp.title, body: [header, sep, ...rows].join('\n') });
      }
    }

    if (topic.content.bestPractices?.length) {
      sections.push({ title: 'Mejores prácticas', body: topic.content.bestPractices.map(bp => `- ${bp}`).join('\n') });
    }

    const instructions: string[] = [topic.description];
    if (topic.quizQuestions?.length) {
      instructions.push('Respondé las preguntas de repaso al final de la lección.');
    }
    if (topic.caseStudies?.length) {
      instructions.push('Analizá los casos de estudio propuestos.');
    }

    return {
      title: topic.title,
      level: topic.level,
      starterCode: '',
      solutionCode: '',
      sampleOutput: '',
      ranking: linkedInRankingData[topic.id],
      theory: {
        heading: topic.title,
        intro: topic.description,
        sections,
      },
      exercise: {
        heading: topic.title,
        instructions,
      },
    };
  });
}

/* ── Registry ── */

export const playgroundCourses: PlaygroundCourse[] = [
  {
    courseId: 'sql',
    courseTitle: 'SQL para Data Engineering',
    language: 'sql',
    lessons: adaptSql(),
    nextCourseId: 'examenes-sql',
  },
  {
    courseId: 'python-intro',
    courseTitle: 'Introducción a Python',
    language: 'python',
    lessons: adaptPythonIntro(),
    nextCourseId: 'python-intermedio',
  },
  {
    courseId: 'python-intermedio',
    courseTitle: 'Python Intermedio',
    language: 'python',
    lessons: adaptPythonIntermedio(),
    nextCourseId: 'python-avanzado',
  },
  {
    courseId: 'python-avanzado',
    courseTitle: 'Python Avanzado',
    language: 'python',
    lessons: adaptPythonAvanzado(),
    nextCourseId: 'examenes-python',
  },
  {
    courseId: 'fundamentos-de',
    courseTitle: 'Fundamentos de Data Engineering',
    language: 'text',
    lessons: adaptDE(),
  },
  {
    courseId: 'examenes-python',
    courseTitle: 'Práctica de Exámenes Python',
    language: 'python',
    lessons: adaptPythonExam(),
  },
  {
    courseId: 'examenes-sql',
    courseTitle: 'Práctica de Exámenes SQL',
    language: 'sql',
    lessons: adaptExamSql(),
    database: 'examentecnico1',
    derImage: '/images/Examen_tecnico.jpg',
  },
  {
    courseId: 'linkedin',
    courseTitle: 'LinkedIn: Posicionamiento de Perfil',
    language: 'text',
    lessons: adaptLinkedIn(),
  },
  {
    courseId: 'bootcamp-de-foundations',
    courseTitle: 'Data Engineering Foundations — Bootcamp',
    language: 'text',
    lessons: adaptBootcampDE(),
  },
];

export function getPlaygroundCourse(courseId: string, cohortFlags?: CohortFlags): PlaygroundCourse | undefined {
  const course = playgroundCourses.find(c => c.courseId === courseId);
  if (!course || courseId !== 'bootcamp-de-foundations' || !cohortFlags) return course;

  return { ...course, lessons: adaptBootcampDE(cohortFlags) };
}
